<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import {
  FieldNodePageFieldSidebarWidgets,
  ParagraphAnchors,
  MediaImageFragment,
  NodeProtectedPage,
} from '~/api/graphql/generated'
import { Link } from '~/types'

interface FormData {
  password: string
  remember: boolean
}

const { pushError } = useNotifications()
const { t } = useI18n()

const props = defineProps<{
  entity: NodeProtectedPage
  breadcrumbs?: Link[]
}>()

const isAuthorized = ref<boolean>(false)

const heroData = computed(() => ({
  content: {
    title: props.entity.title || '',
    subtitle: props.entity.fieldSubtitle || '',
  },
  logo: (props.entity.fieldLogo?.entity as MediaImageFragment) ?? undefined,
  back: props.entity.fieldOverviewLink
    ? {
        text: props.entity.fieldOverviewLink?.title || '',
        url: props.entity.fieldOverviewLink?.url?.path || '',
      }
    : undefined,
  image:
    props.entity.fieldMobileImage?.entity && props.entity.fieldImage?.entity
      ? {
          small: props.entity.fieldMobileImage?.entity as MediaImageFragment,
          large: props.entity.fieldImage?.entity as MediaImageFragment,
        }
      : undefined,
}))

const protectedDetails = computed(() => ({
  password: (props.entity as any).cbzTargetGroup?.entity?.fieldPassword || 'careum8006',
  email: (props.entity as any).cbzTargetGroup?.entity?.fieldEmail || 'schuladministration@careum-bildungszentrum.ch',
  targetGroupId: (props.entity as any).cbzTargetGroup?.entity?.tid.toString() || '0',
}))

provide('targetGroupId', protectedDetails.value.targetGroupId as string)

const login = (data: FormData) => {
  if (data.password === protectedDetails.value.password) {
    isAuthorized.value = true
    const loginCookie = useCookie(`protected-page-${protectedDetails.value.targetGroupId}`, {
      default: () => false,
      watch: 'shallow',
      maxAge: data.remember ? 60 * 60 * 24 * 365 * 2 : 1,
    })
    loginCookie.value = true
  } else {
    pushError(t('protectedPages.popup.invalidPassword'))
  }
}

const displaySearch = computed(() => props.entity.fieldShowSearch)
const searchTitle = computed(() => props.entity.fieldSearchTitle || '')
const searchIntroText = computed(() => props.entity.fieldSearchIntroText?.processed || '')

const introText = computed(() => props.entity.fieldLead?.processed || '')
const loginCookie = useCookie(`protected-page-${protectedDetails.value.targetGroupId}`, {
  default: () => false,
  watch: 'shallow',
})

if (loginCookie.value) {
  isAuthorized.value = true
}
</script>

<template>
  
<FormKitLazyProvider config-file="true">
<template v-if="!isAuthorized">
    <div class="login-wrapper">
      <div v-if="heroData.logo" class="logo">
        <Image v-animate="{ animation: 'vertical-reveal' }" :entity="heroData.logo" type="teaser-main-menu" />
      </div>
      <div class="login-box">
        <h1 class="f-28">{{ entity.title }}</h1>
        <ShopNotifications />
        <FormKit type="form" :actions="false" :incomplete-message="false" @submit="login">
          <Password
            id="protected-page-password"
            :required="true"
            name="password"
            :label="t('protectedPages.popup.label')"
          />
          <Checkbox
            id="protected-page-remember"
            name="remember"
            :label="t('protectedPages.popup.saveLoginLabel')"
            :required="false"
            :value="true"
          />
          <div class="buttons-wrapper">
            <a :href="`mailto:${protectedDetails.email}`">{{ t('protectedPages.popup.email') }}</a>
            <Button primary slide type="submit">{{ t('protectedPages.popup.login') }}</Button>
          </div>
        </FormKit>
      </div>
    </div>
  </template>
  <template v-if="isAuthorized">
    <CbzHero v-bind="heroData" />
    <SectionIntrotext v-if="introText" :text="introText" />
    <CbzSearch
      v-if="displaySearch"
      :target-group="protectedDetails.targetGroupId"
      :title="searchTitle"
      :intro-text="searchIntroText"
    />
    <SectionAnchors
      v-if="entity.fieldAnchors?.entity && !displaySearch"
      :entity="entity.fieldAnchors.entity as ParagraphAnchors"
    />
    <SectionSidebar :sidebar="entity.fieldSidebarWidgets as FieldNodePageFieldSidebarWidgets[]" />
    <Sections :entity="entity" />
  </template>
</FormKitLazyProvider>

</template>
<style scoped lang="scss">
.login-wrapper {

  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 50px;
  z-index: 9999;
  overflow-y: scroll;
  background-color: var(--c-white);
  @include breakpoint(t) {
    display: grid;
    background-color: var(--c-red-100);
    border: 30px solid var(--c-white);
  }
  @include breakpoint(ds) {
    border: 50px solid var(--c-white);
  }
  .logo {
    max-width: 140px;
    @include breakpoint(t) {
      position: absolute;
      top: 30px;
      left: 30px;
    }
    @include breakpoint(ds) {
      top: 48px;
      left: 80px;
    }
  }
}
.login-box {
  background-color: var(--c-white);
  max-width: 700px;
  width: 100%;
  @include breakpoint(t) {
    padding: 30px 60px;
    box-shadow: var(--shadow);
    align-self: center;
    justify-self: center;
  }
  @include breakpoint(ds) {
    padding: 56px 96px 64px 96px;
  }
  :deep(.formkit-outer) {
    margin-bottom: 24px;
  }
  .buttons-wrapper {
    display: flex;
    justify-content: right;
    align-items: center;
    gap: 32px;
  }
}
</style>
